import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { AdminDetails, InvalidAuthTokenError } from '@/lib-backend';
import * as auth from '@/lib-backend/methods/auth';
import {
    SessionExpiredError,
    SetSessionOptions,
} from '@/lib-backend/SessionAdminApiClient';

const AUTH_TOKEN_KEY = 'adm_auth_token';
const REFRESH_TOKEN_KEY = 'adm_refresh_token';
const REMEMBER_ME_KEY = 'adm_remember_me';

function parseJwtExpiry(token: string): Date {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return new Date(payload.exp * 1000);
    } catch {
        return new Date(0);
    }
}

function refreshBeforeMinutes(authToken: string): number {
    const expiresAt = parseJwtExpiry(authToken);
    const lifetimeMin = (expiresAt.getTime() - Date.now()) / 60_000;
    // Refresh at 20% of remaining lifetime, clamped to [0.5, 5] minutes
    return Math.max(0.5, Math.min(5, lifetimeMin * 0.2));
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);

    readonly isLoggedIn = signal(false);
    readonly currentAdmin = signal<AdminDetails | null>(null);
    readonly sessionInitialized = signal(false);

    /**
     * `true` when the current admin has unrestricted (global) access. Non-global
     * admins are scoped to a list of allowed realms — see backend access checks
     * in `Server.Host/Controllers/Admin/{ConfigController,GameController}.cs`.
     */
    readonly isGlobalAdmin = computed(
        () => this.currentAdmin()?.Access._type === 'global',
    );

    private get storage(): Storage {
        return localStorage.getItem(REMEMBER_ME_KEY) === '1'
            ? localStorage
            : sessionStorage;
    }

    async login(
        login: string,
        password: string,
        rememberMe: boolean,
    ): Promise<auth.LoginWithPasswordResult> {
        const response = await this.api.client.call(
            auth.loginWithPassword({ Login: login, Password: password }),
        );

        if (response.Result === 'Ok') {
            if (rememberMe) {
                localStorage.setItem(REMEMBER_ME_KEY, '1');
            } else {
                localStorage.removeItem(REMEMBER_ME_KEY);
            }
            await this.initSession(
                response.AuthToken,
                response.RefreshToken,
                response.Admin,
            );
            this.router.navigate(['/']);
        }

        return response.Result;
    }

    async initSession(
        authToken: string,
        refreshToken: string,
        admin: AdminDetails,
    ): Promise<void> {
        this.storage.setItem(AUTH_TOKEN_KEY, authToken);
        this.storage.setItem(REFRESH_TOKEN_KEY, refreshToken);

        await this.api.client.setSession(authToken, refreshToken, {
            refreshBeforeExpireMinutes: refreshBeforeMinutes(authToken),
            ...this.buildSessionOptions(),
        });

        this.isLoggedIn.set(true);
        this.currentAdmin.set(admin);
    }

    async logout(): Promise<void> {
        try {
            await this.api.client.call(auth.logout());
        } catch {
            // best-effort
        }
        this.api.client.closeSession();
        this.clearTokens();
        localStorage.removeItem(REMEMBER_ME_KEY);
        this.isLoggedIn.set(false);
        this.currentAdmin.set(null);
        this.router.navigate(['/auth/login']);
    }

    async restoreSession(): Promise<void> {
        try {
            await this._doRestoreSession();
        } finally {
            this.sessionInitialized.set(true);
        }
    }

    private async _doRestoreSession(): Promise<void> {
        const store = this.storage;
        let authToken = store.getItem(AUTH_TOKEN_KEY);
        let refreshToken = store.getItem(REFRESH_TOKEN_KEY);

        if (!authToken || !refreshToken) return;

        const sessionOptions = this.buildSessionOptions();

        // setSession calls me() internally to validate the auth token.
        // If the token is already expired it throws InvalidAuthTokenError —
        // in that case attempt a silent token refresh before giving up.
        try {
            await this.api.client.setSession(authToken, refreshToken, {
                refreshBeforeExpireMinutes: refreshBeforeMinutes(authToken),
                ...sessionOptions,
            });
        } catch (e) {
            if (!(e instanceof InvalidAuthTokenError)) return;

            // Auth token expired — try refreshing it using rawClient
            const refreshed = await this.tryRefreshTokens(
                authToken,
                refreshToken,
            );
            if (!refreshed) {
                this.clearTokens();
                return;
            }

            authToken = refreshed.authToken;
            refreshToken = refreshed.refreshToken;
            store.setItem(AUTH_TOKEN_KEY, authToken);
            store.setItem(REFRESH_TOKEN_KEY, refreshToken);

            try {
                await this.api.client.setSession(authToken, refreshToken, {
                    refreshBeforeExpireMinutes: refreshBeforeMinutes(authToken),
                    ...sessionOptions,
                });
            } catch {
                this.clearTokens();
                return;
            }
        }

        try {
            const meResponse = await this.api.client.call(auth.me());
            this.isLoggedIn.set(true);
            this.currentAdmin.set(meResponse.Admin);
        } catch {
            this.clearTokens();
        }
    }

    /**
     * Current admin auth token for callers that talk to non-Platform services
     * (e.g. the AI Assist service) with a raw Bearer header, bypassing
     * `SessionAdminApiClient`.
     *
     * The lib session refreshes tokens lazily — only when a call goes through
     * `SessionAdminApiClient.call()`. During long stretches of non-Platform
     * traffic (e.g. polling an image job for minutes) the stored token would
     * silently expire. So when the token is due for refresh (or on
     * `forceRefresh`), poke the session with a cheap `me()` call: the session
     * refreshes under its own mutex and `onTokensRefreshed` writes the new
     * token back to storage, which we then re-read.
     *
     * Returns null when there is no live session (caller should surface an
     * auth error; `onSessionClosed` has already redirected to login).
     */
    async getFreshAuthToken(options?: {
        forceRefresh?: boolean;
    }): Promise<string | null> {
        const token = this.storage.getItem(AUTH_TOKEN_KEY);
        if (!token) return null;

        const refreshAt =
            parseJwtExpiry(token).getTime() -
            refreshBeforeMinutes(token) * 60_000;
        if (!options?.forceRefresh && Date.now() < refreshAt) return token;

        try {
            await this.api.client.call(auth.me());
        } catch {
            // Session expired or refresh failed — onSessionClosed has already
            // cleared the tokens; fall through and re-read storage (→ null).
        }
        return this.storage.getItem(AUTH_TOKEN_KEY);
    }

    private buildSessionOptions(): SetSessionOptions {
        return {
            onTokensRefreshed: (sessionInfo) => {
                this.storage.setItem(AUTH_TOKEN_KEY, sessionInfo.authToken);
                this.storage.setItem(
                    REFRESH_TOKEN_KEY,
                    sessionInfo.refreshToken,
                );
            },
            onSessionClosed: () => {
                const wasLoggedIn = this.isLoggedIn();
                this.clearTokens();
                this.isLoggedIn.set(false);
                this.currentAdmin.set(null);
                // Navigate only if the user was actively logged in.
                // During restoreSession isLoggedIn is still false, so no premature redirect.
                if (wasLoggedIn) {
                    this.router.navigate(['/auth/login']);
                }
            },
        };
    }

    private async tryRefreshTokens(
        oldAuthToken: string,
        refreshToken: string,
    ): Promise<{ authToken: string; refreshToken: string } | null> {
        try {
            // Pass the old (possibly expired) auth token — the server may require it on the refresh endpoint
            const resp = await this.api.rawClient.call(
                auth.refreshAuthToken({ RefreshToken: refreshToken }),
                oldAuthToken,
            );
            if (resp.Result !== 'Ok') return null;
            return {
                authToken: resp.AuthToken,
                refreshToken: resp.RefreshToken,
            };
        } catch {
            return null;
        }
    }

    private clearTokens(): void {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    }
}
